import { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  MapPin,
  Video,
  Phone,
  CheckCircle,
  Loader2,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ArrowRight,
  HelpCircle,
  Languages,
  Sparkles,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { submitContactForm } from '@/services/contactApiService';
import { trackFormSubmitted } from '@/lib/analytics/formEvents';
import {
  format,
  addDays,
  addMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  isBefore,
  isAfter,
  startOfDay,
} from 'date-fns';
import { es } from 'date-fns/locale';
import useInteractionTracker from '@/hooks/useInteractionTracker';
import { isGlattparkHoliday } from '@/config/holidaysGlattpark';

// Import custom icons
import iconGestoria from '@/assets/icons/gestoria.png';
import iconLegal from '@/assets/icons/legal.png';
import iconSeguros from '@/assets/icons/seguros.png';
import iconDigital from '@/assets/icons/digital.png';
import iconRelocation from '@/assets/icons/relocation.png';
import iconViviendas from '@/assets/icons/viviendas.png';

interface AgendarConsultaModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedService?: string;
}

const TOTAL_STEPS = 5;

const servicios = [
  { id: 'gestoria', name: 'Gestoría y Administración', shortName: 'Gestoría', icon: iconGestoria, isImage: true },
  { id: 'legal', name: 'Asesoría Legal', shortName: 'Legal', icon: iconLegal, isImage: true },
  { id: 'idiomas', name: 'Formación en Idiomas', shortName: 'Idiomas', icon: null, isImage: false, lucideIcon: 'languages' },
  { id: 'seguros', name: 'Seguros y Pensiones', shortName: 'Seguros', icon: iconSeguros, isImage: true },
  { id: 'digital', name: 'Soluciones Digitales', shortName: 'Digital', icon: iconDigital, isImage: true },
  { id: 'relocation', name: 'Pack Relocation', shortName: 'Relocation', icon: iconRelocation, isImage: true },
  { id: 'viviendas', name: 'Alquiler de Viviendas', shortName: 'Viviendas', icon: iconViviendas, isImage: true },
  { id: 'otro', name: 'Otro / No estoy seguro', shortName: 'Otro', icon: null, isImage: false, lucideIcon: 'help' },
];

const modalidades = [
  { id: 'presencial', name: 'Presencial (Oficina Zurich)', icon: MapPin },
  { id: 'videollamada', name: 'Videollamada', icon: Video },
  { id: 'telefono', name: 'Llamada telefónica', icon: Phone },
];

// Available time slots — consultas gratuitas de 15 minutos
// Horario de atención: 08:30 a 16:30 (último slot 16:30), tramos de 15 min.
const timeSlots = (() => {
  const slots: string[] = [];
  const start = 8 * 60 + 30; // 08:30
  const end = 16 * 60 + 30;  // 16:30 inclusive
  for (let m = start; m <= end; m += 15) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    slots.push(`${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`);
  }
  return slots;
})();

// Duración de la consulta gratuita (minutos)
const CONSULTA_DURACION_MIN = 15;
// Anticipación mínima para agendar (horas) — 3 días laborables
const MIN_ANTICIPACION_HORAS = 72;

const AgendarConsultaModal = ({ isOpen, onClose, preselectedService }: AgendarConsultaModalProps) => {
  const { trackInteraction } = useInteractionTracker();

  const [step, setStep] = useState(preselectedService ? 2 : 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const today = startOfDay(new Date());
  // Mínimo 3 días laborables de antelación (excluye fines de semana y festivos de Glattpark 8152)
  const minDate = useMemo(() => {
    let date = startOfDay(new Date());
    let added = 0;
    while (added < 3) {
      date = addDays(date, 1);
      const d = date.getDay();
      if (d !== 0 && d !== 6 && !isGlattparkHoliday(date)) added += 1;
    }
    // Si el propio minDate cae en festivo (caso borde), avanzar
    while (isGlattparkHoliday(date) || date.getDay() === 0 || date.getDay() === 6) {
      date = addDays(date, 1);
    }
    return date;
  }, []);

  // Próxima cita disponible: primer día laborable ≥ minDate a las 09:00
  const proximaCitaLabel = useMemo(() => {
    const fecha = format(minDate, "EEEE d 'de' MMMM", { locale: es });
    return `${fecha} · ${timeSlots[0]}`;
  }, [minDate]);

  // Próximos 6 huecos válidos (un slot por día laborable, empezando a las 09:00)
  const quickSlots = useMemo(() => {
    const slots: { date: Date; time: string; key: string }[] = [];
    let date = minDate;
    const limit = addDays(today, 60);
    while (slots.length < 6 && !isAfter(date, limit)) {
      const d = date.getDay();
      if (d !== 0 && d !== 6 && !isGlattparkHoliday(date)) {
        slots.push({
          date,
          time: timeSlots[0],
          key: `${format(date, 'yyyy-MM-dd')}-${timeSlots[0]}`,
        });
      }
      date = addDays(date, 1);
    }
    return slots;
  }, [minDate, today]);

  const maxDate = addDays(today, 60); // Allow booking up to 60 days ahead

  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));

  const [formData, setFormData] = useState({
    servicio: preselectedService || '',
    fecha: null as Date | null,
    hora: '',
    modalidad: '',
    nombre: '',
    email: '',
    telefono: '',
    mensaje: '',
  });

  // Update state when preselectedService changes or modal opens
  useEffect(() => {
    if (!isOpen) return;

    // When opening, reset the flow to the correct starting step
    setIsSuccess(false);
    setIsSubmitting(false);
    setCurrentMonth(startOfMonth(new Date()));

    if (preselectedService) {
      setFormData({
        servicio: preselectedService,
        fecha: null,
        hora: '',
        modalidad: '',
        nombre: '',
        email: '',
        telefono: '',
        mensaje: '',
      });
      setStep(2);
    } else {
      setFormData({
        servicio: '',
        fecha: null,
        hora: '',
        modalidad: '',
        nombre: '',
        email: '',
        telefono: '',
        mensaje: '',
      });
      setStep(1);
    }
  }, [isOpen, preselectedService]);

  // ===== Disponibilidad real del calendario =====
  // Cuando se selecciona una fecha, consultamos los huecos libres reales
  // (citas existentes + bloqueos) en `get-calendar-availability`.
  const [availableTimes, setAvailableTimes] = useState<Set<string> | null>(null);
  const [availabilityStatus, setAvailabilityStatus] = useState<'idle' | 'loading' | 'ok' | 'empty' | 'error'>('idle');

  useEffect(() => {
    if (!formData.fecha) {
      setAvailableTimes(null);
      setAvailabilityStatus('idle');
      return;
    }

    let cancelled = false;
    const fechaStr = format(formData.fecha, 'yyyy-MM-dd');
    setAvailabilityStatus('loading');
    setAvailableTimes(null);

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-calendar-availability', {
          body: {
            startDate: `${fechaStr}T00:00:00.000Z`,
            endDate: `${fechaStr}T23:59:59.999Z`,
            duration: CONSULTA_DURACION_MIN,
            businessStartMinutes: 8 * 60 + 30, // 08:30
            businessEndMinutes: 16 * 60 + 45,  // último slot 16:30 + 15 min
            slotIntervalMinutes: 15,
          },
        });

        if (cancelled) return;
        if (error) throw error;

        const slots: Array<{ start: string }> = data?.slots ?? [];
        const times = new Set(
          slots
            .map((s) => {
              const d = new Date(s.start);
              return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            })
            .filter((t) => timeSlots.includes(t))
        );

        setAvailableTimes(times);
        setAvailabilityStatus(times.size === 0 ? 'empty' : 'ok');

        // Si la hora elegida ya no está libre, limpiamos para forzar nueva selección
        if (formData.hora && !times.has(formData.hora)) {
          setFormData((prev) => ({ ...prev, hora: '' }));
        }
      } catch (e) {
        if (cancelled) return;
        console.error('[AgendarConsulta] availability error', e);
        setAvailabilityStatus('error');
        setAvailableTimes(null);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.fecha]);


  const monthDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [currentMonth]);

  const isDateDisabled = (date: Date) => {
    const day = date.getDay();
    // Desactiva fines de semana, festivos de Glattpark (8152), fechas con menos de 3 días laborables de antelación, y fechas más allá de maxDate
    return day === 0 || day === 6 || isGlattparkHoliday(date) || isBefore(date, minDate) || isAfter(date, maxDate);
  };

  const canGoPrevMonth = useMemo(() => {
    const prevMonth = addMonths(currentMonth, -1);
    return isAfter(endOfMonth(prevMonth), today);
  }, [currentMonth, today]);

  const canGoNextMonth = useMemo(() => {
    const nextMonth = addMonths(currentMonth, 1);
    return isBefore(startOfMonth(nextMonth), maxDate);
  }, [currentMonth, maxDate]);

  const goToPrevMonth = () => {
    if (!canGoPrevMonth) return;
    setCurrentMonth(addMonths(currentMonth, -1));
  };

  const goToNextMonth = () => {
    if (!canGoNextMonth) return;
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const handleServiceSelect = (servicioId: string) => {
    setFormData(prev => ({
      ...prev,
      servicio: servicioId,
      fecha: null,
      hora: '',
      modalidad: '',
    }));
    setStep(2);
  };

  const handleDateSelect = (date: Date) => {
    setFormData(prev => ({
      ...prev,
      fecha: date,
      hora: '',
      modalidad: '',
    }));
    setStep(3);
  };

  const handleTimeSelect = (time: string) => {
    setFormData(prev => ({ ...prev, hora: time, modalidad: '' }));
    setStep(4);
  };

  const handleModalidadSelect = (modalidadId: string) => {
    setFormData(prev => ({ ...prev, modalidad: modalidadId }));
    setStep(5);
  };

  // Aplica un hueco sugerido (fecha + hora) y salta directamente a modalidad.
  // Si aún no hay servicio, lleva al usuario al paso 1 para que lo elija primero.
  const applyQuickSlot = (date: Date, time: string) => {
    setCurrentMonth(startOfMonth(date));
    setFormData(prev => ({
      ...prev,
      fecha: date,
      hora: time,
      modalidad: '',
    }));
    setStep(prev => (formData.servicio ? 4 : 1));
  };

  const goBack = () => {
    // If preselected, don't allow going back to step 1
    if (step > 1 && !(step === 2 && preselectedService)) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const servicioNombre = servicios.find(s => s.id === formData.servicio)?.name || formData.servicio;
      const modalidadNombre = modalidades.find(m => m.id === formData.modalidad)?.name || formData.modalidad;

      const fechaStr = formData.fecha ? format(formData.fecha, 'yyyy-MM-dd') : '';

      const mensajeCompleto = `[CITA AGENDADA] Servicio: ${servicioNombre} | Fecha: ${fechaStr} ${formData.hora} | Modalidad: ${modalidadNombre} | ${formData.mensaje || 'Sin mensaje'}`;

      // 1) PRIMARY: Use centralized flow (local DB → CRM → emails)
      await submitContactForm({
        nombre: formData.nombre,
        email: formData.email,
        telefono: formData.telefono || undefined,
        mensaje: mensajeCompleto,
        servicio: servicioNombre,
        type: 'appointment',
      });

      // 2) SECONDARY: Save to appointments table for calendar view (non-blocking)
      const fechaInicio = new Date(`${fechaStr}T${formData.hora}:00`);
      const fechaFin = new Date(fechaInicio.getTime() + CONSULTA_DURACION_MIN * 60 * 1000);

      supabase.from('appointments').insert({
        title: `Consulta 15min: ${servicioNombre} - ${formData.nombre}`,
        start_time: fechaInicio.toISOString(),
        end_time: fechaFin.toISOString(),
        status: 'pending',
        service_type: formData.servicio,
        is_virtual: formData.modalidad !== 'presencial',
        location: formData.modalidad === 'presencial' ? 'Oficina Zurich' : modalidadNombre,
        notes: formData.mensaje || null,
        metadata: {
          source: 'website_agendar_consulta',
          duration_min: CONSULTA_DURACION_MIN,
          servicio_id: formData.servicio,
          servicio_nombre: servicioNombre,
          modalidad_id: formData.modalidad,
          modalidad_nombre: modalidadNombre,
        },
      }).then(({ error }) => {
        if (error) console.warn('[AgendarConsulta] appointments insert failed:', error);
      });

      // 2b) Trigger AI briefing → PDF → Google Calendar → email a la gestora (no bloqueante)
      supabase.functions.invoke('process-free-consultation', {
        body: {
          nombre: formData.nombre,
          email: formData.email,
          telefono: formData.telefono,
          servicio_id: formData.servicio,
          servicio_nombre: servicioNombre,
          modalidad_id: formData.modalidad,
          modalidad_nombre: modalidadNombre,
          fecha_inicio: fechaInicio.toISOString(),
          fecha_fin: fechaFin.toISOString(),
          mensaje: formData.mensaje || '',
        },
      }).then(({ error }) => {
        if (error) console.warn('[AgendarConsulta] process-free-consultation failed:', error);
      });

      // 3) Centralized tracking
      await trackInteraction({
        interaction_type: 'appointment_booked',
        customer_name: formData.nombre,
        customer_email: formData.email,
        customer_phone: formData.telefono,
        metadata: {
          servicio_id: formData.servicio,
          servicio_nombre: servicioNombre,
          fecha: fechaStr,
          hora: formData.hora,
          modalidad_id: formData.modalidad,
          modalidad_nombre: modalidadNombre,
        },
      });

      setIsSuccess(true);
      trackFormSubmitted({
        form_id: 'agendar_consulta',
        service_area: formData.servicio,
        has_phone: Boolean(formData.telefono),
        source: 'modal',
      });
      toast.success('¡Consulta agendada correctamente!');

      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setStep(preselectedService ? 2 : 1);
        setFormData({
          servicio: preselectedService || '',
          fecha: null,
          hora: '',
          modalidad: '',
          nombre: '',
          email: '',
          telefono: '',
          mensaje: '',
        });
      }, 2500);
    } catch (error) {
      console.error('Error al agendar consulta:', error);
      toast.error('Error al agendar. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const servicioLabel = servicios.find(s => s.id === formData.servicio)?.name;
  const modalidadLabel = modalidades.find(m => m.id === formData.modalidad)?.name;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-2xl max-h-[85vh] sm:max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 bg-gradient-to-r from-punto to-punto-dark flex-shrink-0">
              <button
                onClick={onClose}
                aria-label="Cerrar"
                className="absolute top-2.5 sm:top-3.5 right-2.5 sm:right-3.5 h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 active:bg-white/30 transition-colors"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </button>
              <h2 className="text-base sm:text-xl font-semibold text-white pr-12 leading-tight tracking-tight">
                Agendar Consulta Gratuita
                <span className="text-white/70 font-normal"> · 15 min</span>
              </h2>
              <div className="mt-1 text-white/80 text-[11px] sm:text-sm leading-snug">
                <div>Paso {step} de {TOTAL_STEPS}</div>
                <button
                  type="button"
                  onClick={() => applyQuickSlot(quickSlots[0].date, quickSlots[0].time)}
                  className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-white/15 hover:bg-white/25 active:bg-white/30 transition-colors px-2.5 py-1 text-left"
                  aria-label={`Reservar próxima cita disponible: ${proximaCitaLabel}`}
                >
                  <Sparkles className="w-3 h-3 text-white/80 flex-shrink-0" />
                  <span className="text-white/70 text-[11px] sm:text-xs">Próxima cita:</span>
                  <span className="text-white font-medium capitalize text-[11px] sm:text-xs">{proximaCitaLabel}</span>
                  <ArrowRight className="w-3 h-3 text-white/80 flex-shrink-0" />
                </button>
              </div>

              {/* Progress bar */}
              <div className="flex gap-1 sm:gap-1.5 mt-2.5 sm:mt-4">
                {Array.from({ length: TOTAL_STEPS }, (_, idx) => idx + 1).map(i => (
                  <div
                    key={i}
                    className={`h-1 sm:h-1.5 flex-1 rounded-full transition-colors ${i <= step ? 'bg-white' : 'bg-white/30'}`}
                  />
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0">
              <AnimatePresence mode="wait">
                {isSuccess ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center py-12 text-center"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', delay: 0.1 }}
                      className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-6"
                    >
                      <CheckCircle className="w-12 h-12 text-green-600" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Consulta Agendada!</h3>
                    <p className="text-gray-600">Te contactaremos pronto para confirmar tu cita.</p>
                    {formData.fecha && (
                      <div className="mt-4 px-4 py-2 bg-gray-100 rounded-lg">
                        <p className="text-sm text-gray-600">
                          {format(formData.fecha, "EEEE d 'de' MMMM", { locale: es })} a las {formData.hora}
                        </p>
                      </div>
                    )}
                  </motion.div>
                ) : step === 1 ? (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      ¿Qué servicio te interesa?
                    </h3>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      {servicios.map(servicio => (
                        <button
                          key={servicio.id}
                          onClick={() => handleServiceSelect(servicio.id)}
                          className={`p-2.5 sm:p-4 rounded-xl border-2 transition-all active:scale-[0.98] sm:hover:scale-[1.02] flex items-center gap-2 sm:gap-3 min-w-0 ${
                            formData.servicio === servicio.id
                              ? 'border-punto bg-punto/10 text-gray-900'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-punto/50 hover:bg-gray-50'
                          }`}
                        >
                          <div className="w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-gray-50">
                            {servicio.isImage && servicio.icon ? (
                              <img
                                src={servicio.icon}
                                alt=""
                                aria-hidden="true"
                                className="w-7 h-7 sm:w-8 sm:h-8 object-contain"
                                loading="lazy"
                              />
                            ) : servicio.lucideIcon === 'languages' ? (
                              <Languages className="w-5 h-5 sm:w-6 sm:h-6 text-punto" />
                            ) : (
                              <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6 text-punto" />
                            )}
                          </div>
                          <span className="flex-1 min-w-0 text-left leading-tight">
                            <span className="block sm:hidden text-[13px] font-semibold truncate">{servicio.shortName}</span>
                            <span className="hidden sm:block text-sm font-medium">{servicio.name}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                ) : step === 2 ? (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Selecciona una fecha</h3>

                    {/* Búsqueda rápida: próximos huecos válidos */}
                    <div className="rounded-xl border border-punto/20 bg-punto/5 p-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Sparkles className="w-3.5 h-3.5 text-punto flex-shrink-0" />
                        <p className="text-[11px] sm:text-xs font-semibold text-punto uppercase tracking-wide">
                          Sugerencias rápidas
                        </p>
                      </div>
                      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory">
                        {quickSlots.map((slot, idx) => {
                          const selected = formData.fecha && isSameDay(slot.date, formData.fecha) && formData.hora === slot.time;
                          return (
                            <button
                              key={slot.key}
                              onClick={() => applyQuickSlot(slot.date, slot.time)}
                              className={`flex-shrink-0 snap-start rounded-lg px-3 py-2 text-left transition-all border ${
                                selected
                                  ? 'bg-punto text-white border-punto shadow-md'
                                  : 'bg-white border-gray-200 hover:border-punto/60 hover:bg-punto/10 text-gray-700'
                              }`}
                            >
                              <div className="text-[10px] uppercase tracking-wide opacity-70 capitalize">
                                {format(slot.date, 'EEE', { locale: es })}
                                {idx === 0 && <span className="ml-1 font-semibold">· Antes</span>}
                              </div>
                              <div className="text-sm font-semibold capitalize leading-tight">
                                {format(slot.date, "d MMM", { locale: es })}
                              </div>
                              <div className="text-[11px] opacity-80">{slot.time}</div>
                            </button>
                          );
                        })}
                      </div>
                      <p className="mt-2 text-[10px] sm:text-[11px] text-gray-500">
                        Toca una sugerencia para reservar al instante · o elige otro día abajo
                      </p>
                    </div>

                    {/* Apple-style month calendar */}
                    <div className="rounded-2xl p-3 sm:p-4 bg-white border border-gray-100 shadow-sm">
                      {/* Month navigation */}
                      <div className="flex items-center justify-between mb-3 sm:mb-4">
                        <span className="font-semibold text-gray-900 text-[15px] sm:text-base capitalize tracking-tight">
                          {format(currentMonth, 'MMMM yyyy', { locale: es })}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={goToPrevMonth}
                            aria-label="Mes anterior"
                            className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                            disabled={!canGoPrevMonth}
                          >
                            <ChevronLeft className="w-4 h-4 text-punto" />
                          </button>
                          <button
                            onClick={goToNextMonth}
                            aria-label="Mes siguiente"
                            className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                            disabled={!canGoNextMonth}
                          >
                            <ChevronRight className="w-4 h-4 text-punto" />
                          </button>
                        </div>
                      </div>

                      {/* Week days header */}
                      <div className="grid grid-cols-7 mb-1">
                        {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((day, i) => {
                          const isWeekend = i >= 5;
                          return (
                            <div
                              key={day}
                              className={`text-center text-[11px] sm:text-xs font-medium py-1.5 uppercase tracking-wide ${
                                isWeekend ? 'text-gray-300' : 'text-gray-400'
                              }`}
                            >
                              {day}
                            </div>
                          );
                        })}
                      </div>

                      {/* Month grid */}
                      <div className="grid grid-cols-7 gap-y-1">
                        {monthDays.map(date => {
                          const disabled = isDateDisabled(date);
                          const selected = formData.fecha && isSameDay(date, formData.fecha);
                          const isToday = isSameDay(date, today);
                          const inMonth = isSameMonth(date, currentMonth);

                          return (
                            <div key={date.toISOString()} className="flex items-center justify-center py-0.5">
                              <button
                                onClick={() => !disabled && handleDateSelect(date)}
                                disabled={disabled}
                                aria-label={format(date, "EEEE d 'de' MMMM", { locale: es })}
                                aria-pressed={selected || undefined}
                                className={`
                                  relative h-9 w-9 sm:h-10 sm:w-10 rounded-full flex flex-col items-center justify-center text-[15px] sm:text-base font-normal transition-all
                                  ${!inMonth ? 'opacity-0 pointer-events-none' : ''}
                                  ${disabled
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : selected
                                      ? 'bg-punto text-white font-semibold shadow-md'
                                      : isToday
                                        ? 'text-punto font-semibold hover:bg-punto/10'
                                        : 'text-gray-900 hover:bg-gray-100 active:bg-gray-200'
                                  }
                                `}
                              >
                                <span>{format(date, 'd')}</span>
                                {isToday && !selected && (
                                  <span className="absolute bottom-1 h-1 w-1 rounded-full bg-punto" />
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {!preselectedService && (
                      <button
                        onClick={goBack}
                        className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Volver a servicios
                      </button>
                    )}
                  </motion.div>
                ) : step === 3 ? (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Selecciona una hora</h3>

                    {formData.fecha && (
                      <div className="flex items-center gap-2 text-gray-700">
                        <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span className="font-medium text-sm sm:text-base capitalize">
                          {format(formData.fecha, "EEEE d 'de' MMMM", { locale: es })}
                        </span>
                      </div>
                    )}

                    {/* Aviso de disponibilidad real del calendario */}
                    {availabilityStatus === 'loading' && (
                      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs sm:text-sm text-gray-600">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Verificando disponibilidad real del calendario…
                      </div>
                    )}
                    {availabilityStatus === 'empty' && (
                      <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs sm:text-sm text-amber-900">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-semibold">No hay huecos disponibles en esta fecha.</p>
                          <p className="text-amber-800">El calendario de nuestras gestoras está completo el {format(formData.fecha!, "EEEE d 'de' MMMM", { locale: es })}. Por favor, elija otro día.</p>
                        </div>
                      </div>
                    )}
                    {availabilityStatus === 'error' && (
                      <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs sm:text-sm text-amber-900">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-semibold">No hemos podido verificar el calendario.</p>
                          <p className="text-amber-800">Puede continuar, pero confirmaremos la disponibilidad por correo antes de cerrar la cita.</p>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 sm:gap-2">
                      {timeSlots.map(time => {
                        const busy = availabilityStatus === 'ok' && availableTimes ? !availableTimes.has(time) : false;
                        const disabled = busy || availabilityStatus === 'loading' || availabilityStatus === 'empty';
                        return (
                          <button
                            key={time}
                            onClick={() => !disabled && handleTimeSelect(time)}
                            disabled={disabled}
                            aria-label={busy ? `${time} no disponible` : time}
                            className={`
                              py-2.5 sm:py-3 px-2 rounded-lg text-xs sm:text-sm font-medium transition-all
                              ${formData.hora === time
                                ? 'bg-punto text-white shadow-md'
                                : busy
                                  ? 'bg-gray-100 border border-gray-200 text-gray-400 line-through cursor-not-allowed'
                                  : disabled
                                    ? 'bg-gray-50 border border-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'bg-white border border-gray-200 text-gray-700 hover:border-punto hover:bg-punto/5'
                              }
                            `}
                          >
                            {time}
                          </button>
                        );
                      })}
                    </div>


                    <Button
                      type="button"
                      variant="outline"
                      onClick={goBack}
                      className="w-full border-gray-300 text-gray-700 hover:bg-gray-100 text-xs sm:text-sm h-9 sm:h-10"
                    >
                      <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                      Volver
                    </Button>
                  </motion.div>
                ) : step === 4 ? (
                  <motion.div
                    key="step4"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">Elige la modalidad</h3>

                    <div className="bg-gray-50 rounded-xl p-3 sm:p-4">
                      <h4 className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Tu cita</h4>
                      <div className="space-y-0.5 sm:space-y-1">
                        <p className="font-semibold text-gray-900 text-sm sm:text-base">{servicioLabel}</p>
                        <p className="text-gray-600 text-sm capitalize">
                          {formData.fecha && format(formData.fecha, "EEEE d 'de' MMMM 'de' yyyy", { locale: es })}
                        </p>
                        <p className="text-punto font-semibold text-sm sm:text-base">{formData.hora}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                      {modalidades.map(modalidad => {
                        const Icon = modalidad.icon;
                        return (
                          <button
                            key={modalidad.id}
                            onClick={() => handleModalidadSelect(modalidad.id)}
                            className={`
                              p-2.5 sm:p-4 rounded-xl border-2 flex flex-col items-center gap-1 sm:gap-2 transition-all
                              ${formData.modalidad === modalidad.id
                                ? 'border-punto bg-punto/10'
                                : 'border-gray-200 hover:border-punto/50 hover:bg-gray-50'
                              }
                            `}
                          >
                            <Icon
                              className={`w-5 h-5 sm:w-6 sm:h-6 ${
                                formData.modalidad === modalidad.id ? 'text-punto' : 'text-gray-500'
                              }`}
                            />
                            <span className="text-[10px] sm:text-sm font-medium text-gray-700 text-center leading-tight">
                              {modalidad.id === 'presencial' ? (
                                <>
                                  <span className="sm:hidden">Oficina</span>
                                  <span className="hidden sm:inline">{modalidad.name}</span>
                                </>
                              ) : modalidad.id === 'videollamada' ? (
                                <>
                                  <span className="sm:hidden">Video</span>
                                  <span className="hidden sm:inline">{modalidad.name}</span>
                                </>
                              ) : (
                                <>
                                  <span className="sm:hidden">Llamada</span>
                                  <span className="hidden sm:inline">{modalidad.name}</span>
                                </>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={goBack}
                      className="w-full border-gray-300 text-gray-700 hover:bg-gray-100 text-xs sm:text-sm h-9 sm:h-10"
                    >
                      <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                      Volver
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div
                    key="step5"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    {/* Summary */}
                    <div className="bg-gray-50 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                      <h4 className="text-xs sm:text-sm font-medium text-gray-500 mb-2">Tu cita</h4>
                      <div className="space-y-0.5 sm:space-y-1">
                        <p className="font-semibold text-gray-900 text-sm sm:text-base">{servicioLabel}</p>
                        <p className="text-gray-600 text-sm capitalize">
                          {formData.fecha && format(formData.fecha, "EEEE d 'de' MMMM 'de' yyyy", { locale: es })}
                        </p>
                        <p className="text-punto font-semibold text-sm sm:text-base">{formData.hora}</p>
                        <p className="text-gray-500 text-xs sm:text-sm">{modalidadLabel}</p>
                      </div>
                    </div>

                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                      Tus datos de contacto
                    </h3>

                    <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                      <div>
                        <Label className="text-gray-700 text-xs sm:text-sm font-medium">Nombre completo *</Label>
                        <Input
                          type="text"
                          value={formData.nombre}
                          onChange={e => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                          className="mt-1 bg-white border-gray-300 text-gray-900 text-sm sm:text-base h-9 sm:h-10"
                          placeholder="Tu nombre"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        <div>
                          <Label className="text-gray-700 text-xs sm:text-sm font-medium">Email *</Label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            className="mt-1 bg-white border-gray-300 text-gray-900 text-sm sm:text-base h-9 sm:h-10"
                            placeholder="tu@email.com"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-gray-700 text-xs sm:text-sm font-medium">Teléfono *</Label>
                          <Input
                            type="tel"
                            value={formData.telefono}
                            onChange={e => setFormData(prev => ({ ...prev, telefono: e.target.value }))}
                            className="mt-1 bg-white border-gray-300 text-gray-900 text-sm sm:text-base h-9 sm:h-10"
                            placeholder="+41..."
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-gray-700 text-xs sm:text-sm font-medium">Mensaje (opcional)</Label>
                        <Textarea
                          value={formData.mensaje}
                          onChange={e => setFormData(prev => ({ ...prev, mensaje: e.target.value }))}
                          className="mt-1 bg-white border-gray-300 text-gray-900 resize-none text-sm sm:text-base"
                          placeholder="¿Algo que debamos saber?"
                          rows={2}
                        />
                      </div>

                      <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={goBack}
                          className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-100 text-xs sm:text-sm h-9 sm:h-10"
                        >
                          <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                          Volver
                        </Button>
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-1 bg-punto hover:bg-punto-dark text-white font-semibold text-xs sm:text-sm h-9 sm:h-10"
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2 animate-spin" />
                              Agendando...
                            </>
                          ) : (
                            'Confirmar Cita'
                          )}
                        </Button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AgendarConsultaModal;
